import { HTMLAttributes, useEffect, useRef } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import gsap from 'gsap'
import Link from 'next/link'

import BioLabel from '@/components/base/bio-label'
import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'
import Breadcrumbs from '@/components/sections/breadcrumbs'
import Spacer from '@/components/sections/spacer'
import {
  CarouselProvider,
  type CarouselProviderProps,
  useCarousel
} from '@/contexts/carousel'
import { useLoaderStore } from '@/store/loader'
import { useRouterStore } from '@/store/router'

import { heroCoverRevealAnimation } from './animations/hero-cover'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type HeroSlideProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    use_carousel: boolean
    breadcrumbs: WPLink[]
  }>

const HeroSlide = ({ use_carousel, breadcrumbs, ...props }: HeroSlideProps) => {
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)
  const { currentSlide, currentSlideIndex } = useCarousel()
  const nodeRef = useRef<HTMLDivElement>(null!)
  const mediaRef = useRef<HTMLImageElement>(null!)
  const active = useRef(false)

  const handleExitTransition = () => {
    setLoaderState({ heroCoverLoaded: false })
    active.current = false
  }

  const handleEnterTransition = () => {
    gsap.set(nodeRef.current, { opacity: 0 })
    active.current = true
  }

  const transitionListeners = (done: () => void) => {
    if (!active.current) {
      heroCoverRevealAnimation(nodeRef.current, false, done)
    } else {
      done()
    }
  }

  useEffect(() => {
    const unsubscribe = useLoaderStore.subscribe(
      (state) => state.heroCoverLoaded,
      (heroCoverLoaded) => {
        if (!heroCoverLoaded) return
        heroCoverRevealAnimation(nodeRef.current, true)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () => setLoaderState({ heroCoverLoaded: false })
    )

    return () => {
      unsubscribe()
    }
  }, [setLoaderState])

  const ContainerWrapper = currentSlide?.button?.url
    ? ({ children }: { children: React.ReactNode }) => (
        <Link
          href={currentSlide.button.url}
          target={currentSlide.button.target}
          scroll={false}
          className="block w-full"
        >
          {children}
        </Link>
      )
    : ({ children }: { children: React.ReactNode }) => <>{children}</>

  return (
    <SwitchTransition mode="out-in">
      <Transition
        appear={true}
        enter={true}
        key={currentSlideIndex}
        nodeRef={nodeRef}
        onExit={handleExitTransition}
        onEnter={handleEnterTransition}
        addEndListener={transitionListeners}
        unmountOnExit
      >
        <div
          ref={nodeRef}
          className={cn('min-h-svh-vh absolute left-0 top-0 w-full')}
          {...props}
        >
          <div className="absolute left-0 top-0 h-full w-full overflow-hidden md:p-spacing-2xs">
            <div className="relative z-10 h-full w-full overflow-hidden md:rounded-radius-rounded">
              <div
                className={cn(
                  'absolute left-0 top-0 h-full w-full',
                  'after:absolute after:left-0 after:top-0 after:z-20 after:h-full after:w-full after:bg-[#000]/30'
                )}
                data-motion-media
              >
                <Media
                  ref={mediaRef}
                  size="full"
                  fill
                  onLoad={() =>
                    setLoaderState({ heroLoaded: true, heroCoverLoaded: true })
                  }
                  {...currentSlide?.media}
                />
              </div>
            </div>
          </div>
          <ContainerWrapper>
            <Container
              className={cn(
                'min-h-svh-vh relative z-10 flex items-end pb-[9rem]',
                'md:pb-[17.8rem]'
              )}
            >
              {(breadcrumbs ?? []).length > 1 ? (
                <div className="absolute left-0 top-header-height z-10 w-full">
                  <Spacer variant="xs" />
                  <Breadcrumbs
                    className={cn([
                      'dark [&_span]:text-text-overlay-heading-light'
                    ])}
                    items={breadcrumbs}
                    data-motion-breadcrumbs
                  />
                </div>
              ) : null}
              <Grid>
                <div
                  className={cn(
                    'col-span-2 md:col-span-4 lg:col-span-6',
                    'grid gap-y-spacing-sm px-margin-sm md:gap-y-spacing-lg lg:px-0'
                  )}
                >
                  <div className={cn('grid grid-cols-3 gap-gutter')}>
                    <div className={cn(['col-span-3', 'relative z-10'])}>
                      <div
                        className={cn([
                          'absolute bottom-0 left-0 z-10 w-full',
                          'flex flex-col items-start gap-y-spacing-sm'
                        ])}
                      >
                        {currentSlide?.pretitle ? (
                          <Typography
                            variant="caption"
                            paragraph
                            className="font-bold uppercase text-text-overlay-heading-light"
                            data-motion-pretitle
                          >
                            {currentSlide?.pretitle}
                          </Typography>
                        ) : null}
                        {currentSlide?.title ? (
                          <Typography
                            variant="h2"
                            component="h1"
                            className="text-text-overlay-heading-light md:font-medium"
                            data-motion-title
                          >
                            {currentSlide?.title}
                          </Typography>
                        ) : null}
                        {currentSlide?.subtitle ? (
                          <Typography
                            variant="large"
                            paragraph
                            className="text-text-overlay-heading-light"
                            data-motion-subtitle
                          >
                            {currentSlide?.subtitle}
                          </Typography>
                        ) : null}
                        {currentSlide?.paragraph ? (
                          <Typography
                            variant="regular"
                            paragraph
                            className={cn('text-text-overlay-heading-light', {
                              'lg:hidden': use_carousel
                            })}
                            data-motion-text
                          >
                            {currentSlide?.paragraph}
                          </Typography>
                        ) : null}
                        {currentSlide?.show_bio_label ? (
                          <BioLabel data-motion-label />
                        ) : null}
                        {currentSlide?.button && currentSlide.button?.url ? (
                          <div>
                            <Button
                              asChild
                              isInverted
                              variant="primary"
                              data-motion-cta
                            >
                              <span>
                                <TextEffect>
                                  <Typography variant="regular">
                                    {currentSlide.button.title}
                                  </Typography>
                                </TextEffect>
                              </span>
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {currentSlide?.paragraph && use_carousel ? (
                      <div
                        className={cn([
                          'hidden lg:block',
                          'lg:absolute lg:bottom-[4.8rem] lg:right-[7.2rem] lg:z-10 lg:max-w-[29rem]'
                        ])}
                      >
                        <Typography
                          variant="large"
                          paragraph
                          className="w-full text-text-overlay-heading-light"
                          data-motion-paragraph
                        >
                          {currentSlide?.paragraph}
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                </div>
              </Grid>
            </Container>
          </ContainerWrapper>
        </div>
      </Transition>
    </SwitchTransition>
  )
}

export type HeroCoverProps = HTMLAttributes<HTMLDivElement> &
  CarouselProviderProps & {
    breadcrumbs?: WPLink[]
  }

const HeroCover = ({
  slides = [],
  className,
  style,
  ...props
}: HeroCoverProps) => {
  return (
    <CarouselProvider slides={slides} use_carousel={props.use_carousel}>
      <Section
        className={cn(
          'min-h-svh-vh relative z-10',
          'before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0',
          'md:before:bottom-spacing-2xs md:before:left-spacing-2xs md:before:right-spacing-2xs md:before:top-spacing-2xs',
          'md:before:rounded-radius-rounded',
          className
        )}
        style={style}
      >
        <HeroSlide {...props} />
      </Section>
    </CarouselProvider>
  )
}

export default HeroCover
